import React from 'react';
import isEqual from 'react-fast-compare';
import _ from 'underscore';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {pluralize} from 'BenchlingUtil';
import DataConnector from 'connect/DataConnector';
import {EntityStoreUser} from 'constants/EntityStoreConstants';
import {
  LabelingStrategy,
  MODAL_LABELING_STRATEGIES,
  NavStoreRegistry,
  Registry,
} from 'constants/RegistryConstants';
import {AuthParentOption} from 'constants/RegistryConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import {BasicFolderItem} from 'folder-items/FileConstants';
import NavStore from 'nav/NavStore';
import notifications from 'notifications';
import * as LabelingStrategiesUtil from 'registry/labeling-strategies/LabelingStrategiesUtil';
import LabelingStrategyInput from 'registry/labeling-strategies/LabelingStrategyInput';
import * as RegistryUtil from 'registry/RegistryUtil';
import * as RegistryValidatorActions from 'registry/RegistryValidatorActions';
import RegistryValidatorStore, {
  RegistryValidatorStoreState,
  ValidationErrorData,
} from 'registry/RegistryValidatorStore';
import RegistryValidatorTable from 'registry/RegistryValidatorTable';
import EntityStore from 'stores/EntityStore';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import {ButtonStyle} from 'ui-library/buttons/Button';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import * as ModalContainer from 'ui-library/ModalContainer';
import Modal, {ModalButton} from 'ui-library/modals/Modal';
import {listToReadable} from 'util/NameUtil';
import {Optional} from 'util/Types';

export function openRegisterFileModal(): void {
  const onRequestClose = (): void => {
    ModalContainer.close(RegisterFileModal);
  };
  ModalContainer.open(
    RegisterFileModal,
    <ConnectedRegisterFileModal currentUser={EntityStore.currentUser()} onRequestClose={onRequestClose} />
  );
}

interface Props {
  currentUser: EntityStoreUser | null;
  onRequestClose: () => void;
}

interface EnhancedProps {
  registryValidatorStoreState: RegistryValidatorStoreState;
  registry?: Registry;
}

class RegisterFileModalConnector extends DataConnector<Props, EnhancedProps> {
  override shouldSubscribeToRedux = false;
  override stores = [NavStore, RegistryValidatorStore];

  enhanceProps(): EnhancedProps {
    const registryValidatorStoreState = this.registryValidatorStore().getState();
    const initialFiles = registryValidatorStoreState.results;
    if (!initialFiles.length) {
      return {registryValidatorStoreState};
    }
    const [firstResult] = initialFiles;
    const registryId = firstResult.tagSchema!.registryId;
    const registry = _.findWhere(this.navStore().getRegistries(), {id: registryId});
    return {
      registryValidatorStoreState,
      registry,
    };
  }
}

interface CollectionState {
  orgCollection: boolean;
  personalCollection: boolean;
}

type RegisterFileModalProps = Props & EnhancedProps;
export type RegisterFileModalState = RegistryValidatorStoreState &
  CollectionState & {
    error: Optional<string>;
    fileIds?: Array<string>;
    labelingStrategy?: LabelingStrategy;
    selectedResultIds?: Array<string>;
    tagSchemaIds?: Array<string>;
    hasRegistryAuthTagSchema?: boolean;
    validLabelingStrategies?: Array<LabelingStrategy>;
  };

class RegisterFileModal extends React.Component<RegisterFileModalProps, RegisterFileModalState> {
  override state: RegisterFileModalState = this.getState();

  override componentDidMount(): void {
    FileBrowserActions.fetchSources();
  }

  override componentDidUpdate(prevProps: RegisterFileModalProps): void {
    if (
      !isEqual(prevProps.registryValidatorStoreState, this.props.registryValidatorStoreState) ||
      !isEqual(prevProps.registry, this.props.registry)
    ) {
      // Condition will ensure the state is updated only once
      this.setState(this.getState());
    }
  }

  override componentWillUnmount(): void {
    RegistryValidatorActions.cancelMove();
  }

  private getState(): RegisterFileModalState {
    const registryValidatorStoreState = this.props.registryValidatorStoreState;
    const defaultCollectionState = {orgCollection: false, personalCollection: false};

    const initialFiles = registryValidatorStoreState.results;
    if (!initialFiles.length) {
      return {
        ...registryValidatorStoreState,
        ...defaultCollectionState,
        error: null,
      };
    }
    const tagSchemas = _.unique(
      initialFiles.flatMap((file) => (file.tagSchema ? [file.tagSchema] : [])),
      ({id}) => id
    );
    // Get all common labeling strategies for schemas of bioentities.
    const validLabelingStrategies = _.intersection(
      ...tagSchemas.map((tagSchema) =>
        LabelingStrategiesUtil.filterLabelingStrategies(MODAL_LABELING_STRATEGIES, tagSchema)
      )
    );
    if (!validLabelingStrategies.length) {
      const tagSchemaNamesString = listToReadable(tagSchemas.map((schema) => `"${schema.name}"`));
      return {
        ...defaultCollectionState,
        error:
          `Entities of ${tagSchemaNamesString} schemas cannot be registered together ` +
          'because they have incompatible naming options. Please register them separately.',
        isLoading: false,
        myCollectionErrorsByFileId: null,
        orgCollectionErrorsByFileId: null,
        results: [],
        addedFileIds: [],
      };
    }
    const tagSchemaIds = tagSchemas.map((tagSchema) => tagSchema.id);
    const hasRegistryAuthTagSchema = tagSchemas.some(
      (tagSchema) => tagSchema.authParentOption === AuthParentOption.FOLDER
    );
    const labelingStrategy = LabelingStrategiesUtil.getDefaultLabelingStrategy(
      validLabelingStrategies,
      tagSchemaIds
    );

    return {
      error: null,
      fileIds: initialFiles.map((file) => file.id),
      labelingStrategy,
      selectedResultIds: [],
      tagSchemaIds,
      hasRegistryAuthTagSchema,
      validLabelingStrategies,
      ...this.getCollectionState(),
      ...registryValidatorStoreState,
    };
  }

  private getRegistryFromResult(result: BasicFolderItem): NavStoreRegistry | undefined {
    const registryId = result.tagSchema!.registryId;
    return _.findWhere(NavStore.getRegistries(), {id: registryId});
  }

  private getCollectionState(): CollectionState {
    return this.props.registry?.disablePersonalCollections
      ? {
          orgCollection: true,
          personalCollection: false,
        }
      : {
          orgCollection: false,
          personalCollection: true,
        };
  }

  private filterResultsByFileId(
    results: Array<BasicFolderItem>,
    fileIdsToInclude: Array<string>
  ): Array<BasicFolderItem> {
    return results.filter((result) => fileIdsToInclude.indexOf(result.id) !== -1);
  }

  private getActiveResults(): Array<BasicFolderItem> {
    return this.filterResultsByFileId(this.state.results, this.state.fileIds ?? []);
  }

  private getRegistryForMove(): Optional<NavStoreRegistry> {
    const [firstActiveResult] = this.getActiveResults();
    if (!firstActiveResult) {
      return null;
    }
    return this.getRegistryFromResult(firstActiveResult);
  }

  private getErrorsByFileId(): {[fileId: string]: ValidationErrorData} | null {
    if (this.state.orgCollection) {
      return this.state.orgCollectionErrorsByFileId;
    } else {
      return this.state.myCollectionErrorsByFileId;
    }
  }

  private hasUnresolvableErrors(): boolean {
    assert(this.getErrorsByFileId() != null, 'Should not call this method if errorsByFileId is null.');
    for (const fileId of Object.keys(this.getErrorsByFileId()!)) {
      const errorData = RegistryUtil.getErrorData(
        this.getErrorsByFileId()![fileId],
        this.state.labelingStrategy!
      );
      if (errorData.errors.length > 0) {
        return true;
      }
    }
    return false;
  }

  private registerFiles = (): void => {
    RegistryValidatorActions.submitMoveWithDataMods(
      this.getActiveResults(),
      RegistryUtil.getWarningCodesFromValidationResponse(this.getErrorsByFileId() ?? {}),
      RegistryUtil.getDuplicateItemApiIdsFromValidationResponse(this.getErrorsByFileId() ?? {}),
      {
        inUserCollection: this.state.personalCollection,
        inOrgCollection: this.state.orgCollection,
        labelingStrategy: this.state.labelingStrategy!,
      }
    );
  };

  private onChangeChecks = (selectedResultIds: Array<string>): void => {
    this.setState({selectedResultIds});
  };

  private removeFilesFromRegistryValidatorTable = (): void => {
    assert(this.state.selectedResultIds!.length !== 0, 'No selected result ids');
    let newFileIds = _.difference(this.state.fileIds ?? [], this.state.selectedResultIds ?? []);
    newFileIds = _.difference(newFileIds, this.state.addedFileIds);
    if (newFileIds.length === 0) {
      RegistryValidatorActions.cancelMove();
    } else {
      this.setState({
        fileIds: newFileIds,
      });
      const newResults = this.filterResultsByFileId(this.state.results, newFileIds);
      RegistryValidatorActions.checkItems(newResults);
    }
  };

  private handleRequestClose = (): void => {
    RegistryValidatorActions.cancelMove();
  };

  private moveIsCancelled(
    errorsByFileId: {[fileId: string]: ValidationErrorData} | null,
    isLoading: boolean
  ): boolean {
    return !errorsByFileId && !isLoading;
  }

  private handleLabelingStrategyChange = (labelingStrategy: LabelingStrategy): void => {
    this.setState({labelingStrategy});
  };

  private renderBanner(): JSX.Element {
    assert(!this.state.isLoading, 'Do not render banner while loading');
    let banner;
    let bannerType;
    if (this.hasUnresolvableErrors()) {
      banner = (
        <div>
          <strong>
            Items marked with <BenchlingIcon icon="error" /> have unresolvable errors.
          </strong>{' '}
          Fix the errors in the files below or remove them to proceed with registration.
        </div>
      );
      bannerType = InlineBannerType.ERROR;
    } else {
      const numItems = this.state.fileIds!.length;
      const itemWord = pluralize(numItems, 'item');
      if (
        this.state.hasRegistryAuthTagSchema &&
        BenchlingConfig.PERMISSIONS_ENABLE_GRANULAR_ENTITY_PERMISSIONS
      ) {
        banner = (
          <div>
            <strong>{`Moving ${numItems} ${itemWord} into registry.`}</strong>
            <p>
              Registering this item will change who can access it. Any user who has access to the registry
              will gain access to the item while users with existing access will lose that access unless they
              have direct access to the item.
            </p>
          </div>
        );
      } else {
        banner = <strong>{`Moving ${numItems} ${itemWord} into registry.`}</strong>;
      }
      bannerType = InlineBannerType.INFO;
    }
    return (
      <div className="u-moderate-spacing-bottom">
        <InlineBanner bannerType={bannerType} supportingInfo={banner} />
      </div>
    );
  }

  private renderCollectionTypes(): JSX.Element | null {
    const registry = this.getRegistryForMove();
    if (!registry || registry.disablePersonalCollections) {
      return null;
    }

    const noCollectionsSelected = !this.state.personalCollection && !this.state.orgCollection;
    return (
      <div className="form-group">
        <div className="required u-bold-font-weight u-tiny-spacing-bottom">Collection</div>
        {noCollectionsSelected ? <span className="u-danger-color"> Please select at least one</span> : null}
        <Row>
          <Col sm={6}>
            <label className="u-normal-font-weight">
              <input
                type="checkbox"
                checked={this.state.personalCollection}
                onChange={(e) => {
                  this.setState({personalCollection: e.target.checked});
                }}
              />
              <span className="u-tiny-spacing-left"> My Collection </span>
            </label>
          </Col>
          <Col sm={6}>
            <label className="u-normal-font-weight">
              <input
                type="checkbox"
                checked={this.state.orgCollection}
                onChange={(e) => {
                  this.setState({orgCollection: e.target.checked});
                }}
              />
              <span className="u-tiny-spacing-left"> Organization Collection</span>
            </label>
          </Col>
        </Row>
      </div>
    );
  }

  private renderRegistryValidatorTable(): JSX.Element {
    assert(!this.state.isLoading, 'Do not render table while loading');
    const selectedFinderResults = this.getActiveResults().map((result) => ({
      ...result,
      checked: this.state.selectedResultIds!.includes(result.id),
    }));
    return (
      <RegistryValidatorTable
        addedFileIds={this.state.addedFileIds}
        disableCheckboxes={this.state.isLoading || !this.hasUnresolvableErrors()}
        errorsByFileId={this.getErrorsByFileId()}
        labelingStrategy={this.state.labelingStrategy!}
        onChangeChecks={this.onChangeChecks}
        onRequestClose={this.handleRequestClose}
        results={selectedFinderResults}
      />
    );
  }

  private renderSettings(): JSX.Element {
    return (
      <Row>
        <Col md={6}>
          <LabelingStrategyInput
            labelingStrategies={this.state.validLabelingStrategies ?? null}
            labelingStrategy={this.state.labelingStrategy ?? null}
            onChange={this.handleLabelingStrategyChange}
            tagSchemaIds={this.state.tagSchemaIds}
          />
        </Col>
      </Row>
    );
  }

  override render(): JSX.Element | null {
    const {isLoading} = this.state;
    if (this.moveIsCancelled(this.getErrorsByFileId(), isLoading)) {
      if (this.state.error) {
        notifications.error(this.state.error, {duration: 0});
      }
      this.props.onRequestClose();
      return null;
    }
    const buttons: Array<ModalButton> =
      !this.state.isLoading && this.hasUnresolvableErrors()
        ? [
            {
              key: 'remove',
              onClick: this.removeFilesFromRegistryValidatorTable,
              disabled:
                _.intersection(this.state.fileIds ?? [], this.state.selectedResultIds ?? []).length === 0,
              style: ButtonStyle.NEGATIVE,
              text: 'Remove Entities',
            },
          ]
        : [];
    buttons.push({
      key: 'register',
      submit: true,
      onClick: this.registerFiles,
      disabled:
        this.state.isLoading ||
        this.hasUnresolvableErrors() ||
        (!this.state.personalCollection && !this.state.orgCollection),
      text: 'Register',
    });
    return (
      <Modal
        title="Register entities"
        onRequestClose={this.handleRequestClose}
        buttons={buttons}
        large={true}
      >
        {(() => {
          if (this.state.isLoading) {
            return (
              <div className="sidepanel-throbberContainer">
                <HorizontalThrobber />
              </div>
            );
          }
          return (
            <div>
              {this.renderBanner()}
              {this.renderCollectionTypes()}
              {this.renderSettings()}
              {this.renderRegistryValidatorTable()}
            </div>
          );
        })()}
      </Modal>
    );
  }
}

const ConnectedRegisterFileModal = RegisterFileModalConnector.connect(RegisterFileModal);
export default ConnectedRegisterFileModal;
